import type { FC, HTMLAttributes, PropsWithChildren } from "react";
import { useRef } from "react";

import { useFocusTrap } from "@ui/hooks/useFocusTrap";
import { useOutsideClick } from "@ui/hooks/useOutsideClick";
import classnames from "classnames";

import { useDrawer } from "../providers";

import styles from "../Drawer.module.scss";

type DrawerContentProps = PropsWithChildren<{
  className?: string;
  isMobileFullScreen?: boolean;
}> &
  HTMLAttributes<HTMLDivElement>;

export const DrawerContent: FC<DrawerContentProps> = ({ className, children, isMobileFullScreen, ...restProps }) => {
  const drawerContentRef = useRef<HTMLDivElement | null>(null);
  const { isOpen, closeDrawer } = useDrawer();

  const onOutsideClick = () => {
    isOpen && closeDrawer();
  };

  useOutsideClick(drawerContentRef, onOutsideClick);
  useFocusTrap({ isActive: isOpen, containerRef: drawerContentRef });

  return (
    <div
      className={classnames([
        styles.drawerContent,
        className,
        {
          [styles.isOpen]: isOpen,
          [styles.isMobileFullScreen]: isMobileFullScreen,
        },
      ])}
      data-testid="drawer-content"
      role="dialog"
      aria-modal="true"
      inert={!isOpen}
      ref={drawerContentRef}
      {...restProps}
    >
      {children}
    </div>
  );
};

DrawerContent.displayName = "DrawerContent";
